import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { TrendingUp } from '@mui/icons-material';
import { RecipeV2Detail, CostSimulationResult } from '../types';
import { applyRoundAndFormat } from '../../../utils';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import { UserContext } from '../../../components/shared/useUser';

interface CostingTabProps {
  recipe: RecipeV2Detail;
  currencyCode?: string;
}

const CostingTab = ({ recipe, currencyCode }: CostingTabProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const { hasPermissionTo } = useContext(UserContext);
  const canSimulate = hasPermissionTo(['RecipeV2CostingRead']);
  const [selectedComponent, setSelectedComponent] = useState<number>(0);
  const [priceChange, setPriceChange] = useState<number>(10);
  const [simulationResult, setSimulationResult] = useState<CostSimulationResult | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);

  const formatCurrency = (value: number | null | undefined) => value != null ? applyRoundAndFormat(value, currencyCode) : '—';

  const components = recipe.components ?? [];
  const totalCost = recipe.costIsComplete ? recipe.totalCost ?? null : null;
  const missingCostItems = recipe.missingCostComponentItemNumbers ?? [];

  const handleSimulate = async () => {
    if (!selectedComponent) return;
    setIsSimulating(true);
    try {
      const result = await api.simulateCost(recipe.id, selectedComponent, priceChange);
      setSimulationResult(result);
    } catch {
      setSimulationResult(null);
    } finally {
      setIsSimulating(false);
    }
  };

  return (
    <Stack spacing={3}>
      {/* Cost breakdown */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>{t('costing.title')}</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('ingredient')}</TableCell>
                <TableCell align="right">{t('costing.unitCost')}</TableCell>
                <TableCell align="right">{t('quantity')}</TableCell>
                <TableCell align="right">{t('costing.lineCost')}</TableCell>
                <TableCell align="right">% of Total Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {components.map((c) => {
                const lineCost = c.costIsComplete && c.costPrice != null ? c.costPrice * c.quantity : null;
                const pct = totalCost != null && totalCost > 0 && lineCost != null ? (lineCost / totalCost) * 100 : null;
                return (
                  <TableRow key={c.id}>
                    <TableCell>{c.componentDescription}</TableCell>
                    <TableCell align="right">{formatCurrency(c.costPrice)}</TableCell>
                    <TableCell align="right">{c.quantity.toFixed(3)}</TableCell>
                    <TableCell align="right">{formatCurrency(lineCost)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={0.5}>
                        <Typography variant="textSM">{pct != null ? `${pct.toFixed(1)}%` : '—'}</Typography>
                        <Box
                          sx={{
                            width: Math.max((pct ?? 0) * 0.8, 4),
                            height: 8,
                            borderRadius: 1,
                            bgcolor: (pct ?? 0) > 30 ? 'error.main' : (pct ?? 0) > 15 ? 'warning.main' : 'success.main',
                          }}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={3}>
                  <Typography fontWeight={600}>{t('costing.totalCost')}</Typography>
                  {!recipe.costIsComplete && (
                    <Typography variant="textXS"
                      color="warning.main">
                      {t('costing.incompleteCost', { items: missingCostItems.join(', ') })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>{formatCurrency(totalCost)}</Typography>
                </TableCell>
                <TableCell align="right">{recipe.costIsComplete && totalCost != null ? '100%' : '—'}</TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={3}>
                  <Typography fontWeight={600}>{t('costing.costPerPortion')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>
                    {recipe.costIsComplete && recipe.costPerPortion != null
                      ? applyRoundAndFormat(recipe.costPerPortion, currencyCode, 4)
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* What-if simulation */}
      {canSimulate && (
        <Paper variant="outlined"
          sx={{ p: 2 }}>
          <Typography variant="textXL"
            gutterBottom>
            <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t('costing.simulateTitle')}
          </Typography>
          <Stack direction="row"
            spacing={2}
            alignItems="center">
            <Select
              size="small"
              value={selectedComponent}
              onChange={(e) => { setSelectedComponent(Number(e.target.value)); setSimulationResult(null); }}
              displayEmpty
              sx={{ minWidth: 250 }}
            >
              <MenuItem value={0}
                disabled>{t('costing.selectIngredient')}</MenuItem>
              {components.map((c) => (
                <MenuItem key={c.id}
                  value={c.id}>{c.componentDescription}</MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              type="number"
              label={t('costing.priceChange')}
              value={priceChange}
              onChange={(e) => { setPriceChange(Number(e.target.value)); setSimulationResult(null); }}
              sx={{ width: 120 }}
              inputProps={{ step: 5 }}
            />
            <Button variant="primary"
              onClick={handleSimulate}
              disabled={!selectedComponent || isSimulating}>
              {t('costing.simulate')}
            </Button>
          </Stack>

          {simulationResult && (
            <Stack direction="row"
              spacing={4}
              sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.originalCost')}</Typography>
                <Typography variant="textXL">{formatCurrency(simulationResult.originalCost)}</Typography>
              </Box>
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.simulatedCost')}</Typography>
                <Typography variant="textXL"
                  color={simulationResult.simulatedCost > simulationResult.originalCost ? 'error.main' : 'success.main'}>
                  {formatCurrency(simulationResult.simulatedCost)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.difference')}</Typography>
                <Typography variant="textXL"
                  color={simulationResult.difference > 0 ? 'error.main' : 'success.main'}>
                  {simulationResult.difference > 0 ? '+' : ''}{formatCurrency(simulationResult.difference)}
                </Typography>
              </Box>
            </Stack>
          )}
        </Paper>
      )}

    </Stack>
  );
};

export default CostingTab;
